import { bus } from '@core/events/eventSystem';
import constants from './constants';
import axios from 'axios';

const api = axios.create({
  baseURL: constants.ENDPOINTS.CARDS.BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

// Fetch countries
bus.on(constants.EVENTS.CARDS.FETCH_COUNTRIES, async () => {
  try {
    const response = await api.get(
      constants.ENDPOINTS.CARDS.COUNTRIES + constants.ENDPOINTS.PARAMS
    );
    bus.emit(
      constants.EVENTS.CARDS.FETCH_COUNTRIES_SUCCESS,
      response.data.data
    );
  } catch (error) {
    bus.emit(constants.EVENTS.CARDS.FETCH_COUNTRIES_ERROR, error);
  }
});

// Fetch cards by country
bus.on(constants.EVENTS.CARDS.FETCH_BY_COUNTRY, async (countryCode = 'BJ') => {
  const url =
    constants.ENDPOINTS.CARDS.BY_COUNTRY.replace('{countryCode}', countryCode) +
    constants.ENDPOINTS.PARAMS;
  try {
    // Parallel fetch: external API and local API
    const [externalApiResponse, customCardsResponse] = await Promise.allSettled(
      [api.get(url), fetch(`${constants.ENDPOINTS.LOCAL_API}/custom-cards`)]
    );

    // Get external API cards
    let externalApiCards = [];
    if (externalApiResponse.status === 'fulfilled') {
      const response = externalApiResponse.value;
      const data = response.data;
      // Check if data.data is an object with products/cards array
      if (data?.data) {
        if (Array.isArray(data.data)) {
          externalApiCards = data.data;
        } else if (Array.isArray(data.data.products)) {
          externalApiCards = data.data.products;
        } else if (Array.isArray(data.data.cards)) {
          externalApiCards = data.data.cards;
        } else {
          // data.data is an object, try to find array property
          const keys = Object.keys(data.data);
          for (const key of keys) {
            if (Array.isArray(data.data[key])) {
              externalApiCards = data.data[key];
              break;
            }
          }
        }
      }
    }

    // Get custom cards from local API
    let customCards = [];
    if (customCardsResponse.status === 'fulfilled') {
      try {
        const customData = await customCardsResponse.value.json();
        const cardsArray = customData.data || customData.cards || [];
        customCards = cardsArray.map((card) => ({
          ...card,
          isCustom: true,
          productId: `${card.id}`,
          productName: card.title,
          logoUrls: card.imageUrl ? [card.imageUrl] : [],
          // Use recipient fields like external API
          fixedRecipientDenominations: [card.price],
          minRecipientDenomination: card.price,
          maxRecipientDenomination: card.price,
          recipientCurrencyCode: card.currency || 'USD',
          recipientCurrencyToSenderCurrencyExchangeRate: 1,
          category: mapCategory(card.category),
          stock: card.stockQuantity || 0,
          allowedCountries: card.allowedCountries || []
        }));
      } catch (e) {
        console.warn('Failed to parse custom cards response:', e);
      }
    }

    // Filter custom cards by selected country
    // Get selected country code safely
    let selectedCountryCode = countryCode; // Default to the countryCode parameter
    try {
      if (window.$stores?.global?.state?.selectedCountry?.countryCode) {
        selectedCountryCode =
          window.$stores.global.state.selectedCountry.countryCode;
      }
    } catch (e) {
      console.warn(
        'Could not get selected country from store, using parameter:',
        countryCode
      );
    }

    const filteredCustomCards = customCards.filter((card) => {
      // If no allowedCountries, show in all countries
      if (!card.allowedCountries || card.allowedCountries.length === 0) {
        return true;
      }
      // Only show if current country is in allowedCountries
      return card.allowedCountries.includes(selectedCountryCode);
    });

    // Merge results: external API cards first, then filtered custom cards
    const mergedCards = [...externalApiCards, ...filteredCustomCards];
    bus.emit(constants.EVENTS.CARDS.FETCH_BY_COUNTRY_SUCCESS, mergedCards);
  } catch (error) {
    bus.emit(constants.EVENTS.CARDS.FETCH_BY_COUNTRY_ERROR, error);
  }
});

// Helper function to map category string to category object
function mapCategory(categoryName) {
  const categoryMap = {
    'Payment Cards': { id: 1, name: 'Payment Cards' },
    Gaming: { id: 3, name: 'Gaming' },
    Entertainment: { id: 4, name: 'Entertainment' },
    Shopping: { id: 5, name: 'Shopping' },
    Other: { id: 6, name: 'Crypto' }
  };
  return categoryMap[categoryName] || { id: 6, name: 'Crypto' };
}

// Buy gift card
bus.on(constants.EVENTS.CARDS.BUY, async (orderData) => {
  // Expected orderData format:
  // {
  //   service: 'ecoxurf',
  //   requestId: string,
  //   currency: 'XOF',
  //   quantity: 1,
  //   unitPrice: number,
  //   id: string,
  //   recipientEmail: string,
  //   senderName: string,
  //   productId: number,
  //   countryCode: string
  // }

  try {
    const response = await api.post(
      constants.ENDPOINTS.CARDS.BUY + constants.ENDPOINTS.PARAMS,
      {
        service: 'ecoxurf',
        ...orderData
      }
    );
    bus.emit(constants.EVENTS.CARDS.BUY_SUCCESS, response.data);
  } catch (error) {
    bus.emit(constants.EVENTS.CARDS.BUY_ERROR, error);
  }
});
